import { useContext, useEffect, useState, type ReactElement } from 'react'
import classnames from 'classnames'
import { AnimatePresence, motion } from 'motion/react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'

const CLASSIX_NOTIFICATION = process.env.NEXT_PUBLIC_CLASSIX_NOTIFICATION || ''
const CLASSIX_NOTIFICATION_ACK_KEY = 'classix:notification:ack'
// Re-show the notification modal once an hour. The most recent ack
// timestamp lives in localStorage; a 1-min poll re-evaluates so a
// user who never reloads still sees the modal again on schedule.
const CLASSIX_NOTIFICATION_INTERVAL_MS = 60 * 60 * 1000
const CLASSIX_NOTIFICATION_POLL_MS = 60 * 1000

import Topbar from '@/components/common/Header/Topbar'
import Header from '@/components/common/Header'
import { useIsSpaceRoute } from '@/hooks/useIsSpaceRoute'
import css from './styles.module.css'
import SafeLoadingError from '../SafeLoadingError'
import Footer from '../Footer'
import SideDrawer from './SideDrawer'
import { useIsSidebarRoute } from '@/hooks/useIsSidebarRoute'
import { TxModalContext } from '@/components/tx-flow'
import { useLoadFeature } from '@/features/__core__'
import { BatchingFeature } from '@/features/batching'
import { AppRoutes } from '@/config/routes'
import HelpMenu from '@/components/common/HelpMenu'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import { useParentSafe } from '@/hooks/useParentSafe'
import SpaceSafeBar from '@/components/common/SpaceSafeBar'
import { useRouterGuard } from '@/hooks/useRouterGuard'
import { useFlowActivationGuard } from '@/hooks/useRouterGuard/activationGuards/useFlowActivationGuard'

const ONBOARDING_ROUTES = [
  AppRoutes.welcome.createSpace,
  AppRoutes.welcome.selectSafes,
  AppRoutes.welcome.inviteMembers,
]

const NO_HEADER_ROUTES = [
  AppRoutes.safeLabsTerms,
  AppRoutes.welcome.index,
  AppRoutes.welcome.createSpace,
  AppRoutes.welcome.selectSafes,
  AppRoutes.welcome.inviteMembers,
  AppRoutes.spaces.createSpace,
]

const PageLayout = ({ pathname, children }: { pathname: string; children: ReactElement }): ReactElement => {
  const [isSidebarRoute, isAnimated] = useIsSidebarRoute(pathname)
  const [isSidebarOpen, setSidebarOpen] = useState<boolean>(true)
  const [isSpacesSidebarExpanded, setSpacesSidebarExpanded] = useState<boolean>(true)
  const [isBatchOpen, setBatchOpen] = useState<boolean>(false)
  const { txFlow, setFullWidth } = useContext(TxModalContext)
  const { BatchSidebar } = useLoadFeature(BatchingFeature)
  const isSafeLabsTermsPage = pathname === AppRoutes.safeLabsTerms
  const hideHeader = NO_HEADER_ROUTES.includes(pathname)
  const isOnboardingRoute = ONBOARDING_ROUTES.includes(pathname)
  const isSpaceRoute = useIsSpaceRoute()
  const parentSafe = useParentSafe()
  const menuToggleHandler = isSidebarRoute ? setSidebarOpen : undefined

  useRouterGuard({ useGuard: useFlowActivationGuard })

  // Hide sidebar when transaction flow is open
  const isSidebarVisible = isSidebarOpen && !txFlow

  useEffect(() => {
    setFullWidth(!isSidebarVisible)
  }, [isSidebarVisible, setFullWidth])

  // Classix notification: full-screen modal that re-fires every
  // CLASSIX_NOTIFICATION_INTERVAL_MS. localStorage holds the last ack
  // timestamp; we re-evaluate on mount and on a 1-min poll so a user
  // who never reloads still sees the modal again on schedule.
  const [classixNotificationOpen, setClassixNotificationOpen] = useState(false)
  useEffect(() => {
    if (!CLASSIX_NOTIFICATION) return
    if (typeof window === 'undefined') return
    const evaluate = () => {
      let lastAck = 0
      try {
        const raw = window.localStorage.getItem(CLASSIX_NOTIFICATION_ACK_KEY)
        const parsed = raw ? Number(raw) : 0
        lastAck = Number.isFinite(parsed) && parsed > 0 ? parsed : 0
      } catch {
        // localStorage may throw in private mode / disabled — treat as never acked
      }
      if (Date.now() - lastAck >= CLASSIX_NOTIFICATION_INTERVAL_MS) {
        setClassixNotificationOpen(true)
      }
    }
    evaluate()
    const id = window.setInterval(evaluate, CLASSIX_NOTIFICATION_POLL_MS)
    return () => window.clearInterval(id)
  }, [])
  const handleClassixNotificationAck = () => {
    setClassixNotificationOpen(false)
    try {
      window.localStorage.setItem(CLASSIX_NOTIFICATION_ACK_KEY, String(Date.now()))
    } catch {
      // ignore — ack not persisted, but the user has seen it this load
    }
  }

  return (
    <>
      {CLASSIX_NOTIFICATION && (
        <Dialog
          open={classixNotificationOpen}
          aria-labelledby="classix-notification-title"
          aria-describedby="classix-notification-text"
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle id="classix-notification-title">Heads up</DialogTitle>
          <DialogContent>
            <DialogContentText id="classix-notification-text">{CLASSIX_NOTIFICATION}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClassixNotificationAck} autoFocus variant="contained">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {!hideHeader && isSpaceRoute && (
        <div className={css.topbar}>
          <Topbar onMenuToggle={menuToggleHandler} />
        </div>
      )}

      {!hideHeader && !isSpaceRoute && (
        <header className={css.header}>
          <Header onMenuToggle={menuToggleHandler} onBatchToggle={setBatchOpen} />
        </header>
      )}

      {isSidebarRoute ? (
        <SideDrawer
          isOpen={isSidebarVisible}
          onToggle={setSidebarOpen}
          onSidebarOpenChange={isSpaceRoute ? setSpacesSidebarExpanded : undefined}
        />
      ) : null}

      <div
        className={classnames(css.main, {
          [css.mainNoSidebar]: !isSidebarVisible || !isSidebarRoute,
          [css.mainAnimated]: isSidebarRoute && isAnimated,
          [css.mainNoHeader]: hideHeader,
          [css.mainSpace]: isSpaceRoute,
          [css.mainSpaceCollapsed]: isSpaceRoute && !isSpacesSidebarExpanded,
        })}
      >
        <div className={css.content}>
          <SafeLoadingError>
            {!hideHeader && parentSafe && <Breadcrumbs />}
            {!hideHeader && !isSpaceRoute && pathname === AppRoutes.home && <SpaceSafeBar />}
            {isOnboardingRoute ? (
              <AnimatePresence mode="wait">
                <motion.div
                  key={pathname}
                  className={css.onboardingMotion}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, delay: 0.2, ease: 'easeInOut' }}
                >
                  {children}
                </motion.div>
              </AnimatePresence>
            ) : (
              children
            )}
          </SafeLoadingError>
        </div>

        <BatchSidebar isOpen={isBatchOpen} onToggle={setBatchOpen} />

        {!isSafeLabsTermsPage && <Footer />}
      </div>

      <HelpMenu />
    </>
  )
}

export default PageLayout
